import { useApp } from "@/App";
import { useFetch } from "@/lib/hooks";
import { api } from "@/lib/api";
import { ItemCard } from "@/components/ItemCard";
import { ReputationBadge } from "@/components/ReputationBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
	User, Calendar, Star, Package, ArrowLeftRight,
	CheckCircle, MessageSquare, TrendingUp,
} from "lucide-react";

export function ProfilePage({ userId }: { userId: string }) {
	const { navigate, currentUserId } = useApp();
	const { data: user, loading } = useFetch(() => api.users.get(userId), [userId]);
	const { data: items } = useFetch(() => api.users.getItems(userId), [userId]);

	const isOwnProfile = userId === currentUserId;
	const availableItems = items?.filter((i) => i.available) || [];
	const successRate = user ? Math.round((user.successfulSwaps / Math.max(user.totalSwaps, 1)) * 100) : 0;

	if (loading) {
		return (
			<div className="max-w-4xl mx-auto space-y-6">
				<div className="h-48 bg-muted rounded-lg animate-pulse" />
				<div className="grid grid-cols-3 gap-4">
					{Array.from({ length: 3 }).map((_, i) => (
						<div key={i} className="h-24 bg-muted rounded-lg animate-pulse" />
					))}
				</div>
			</div>
		);
	}

	if (!user) {
		return (
			<div className="text-center py-12">
				<p className="text-muted-foreground">User not found.</p>
			</div>
		);
	}

	return (
		<div className="max-w-4xl mx-auto space-y-6">
			{/* Profile Header */}
			<Card>
				<CardContent className="pt-6">
					<div className="flex flex-col sm:flex-row items-start gap-5">
						<div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
							<User className="h-10 w-10 text-primary" />
						</div>
						<div className="flex-1 min-w-0">
							<div className="flex items-start justify-between gap-3">
								<div>
									<h1 className="text-2xl font-bold">{user.displayName}</h1>
									<p className="text-muted-foreground">@{user.username}</p>
								</div>
								{!isOwnProfile && (
									<Button size="sm" onClick={() => navigate(`/trade/${userId}`)}>
										<ArrowLeftRight className="h-4 w-4 mr-1.5" />
										Propose Swap
									</Button>
								)}
							</div>
							<p className="text-sm text-muted-foreground mt-2">{user.bio}</p>
							<div className="flex items-center gap-4 mt-3">
								<ReputationBadge reputation={user.reputation} totalSwaps={user.totalSwaps} size="md" />
								<Separator orientation="vertical" className="h-4" />
								<div className="flex items-center gap-1.5 text-sm text-muted-foreground">
									<Calendar className="h-3.5 w-3.5" />
									Joined {user.joinedAt}
								</div>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Stats */}
			<div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
				<Card>
					<CardContent className="pt-5 pb-4 text-center">
						<Package className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
						<p className="text-2xl font-bold">{availableItems.length}</p>
						<p className="text-[10px] text-muted-foreground uppercase tracking-wider">Listed Items</p>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-5 pb-4 text-center">
						<ArrowLeftRight className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
						<p className="text-2xl font-bold">{user.totalSwaps}</p>
						<p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total Swaps</p>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-5 pb-4 text-center">
						<CheckCircle className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
						<p className="text-2xl font-bold">{successRate}%</p>
						<p className="text-[10px] text-muted-foreground uppercase tracking-wider">Success Rate</p>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-5 pb-4 text-center">
						<Star className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
						<p className="text-2xl font-bold">{user.reputation.toFixed(1)}</p>
						<p className="text-[10px] text-muted-foreground uppercase tracking-wider">Rating</p>
					</CardContent>
				</Card>
			</div>

			{/* Reputation Breakdown */}
			<Card>
				<CardHeader className="pb-3">
					<CardTitle className="text-base flex items-center gap-2">
						<TrendingUp className="h-4 w-4" />
						Reputation Breakdown
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-3">
					<div className="space-y-2">
						<div className="flex items-center justify-between text-sm">
							<span className="text-muted-foreground">Swap Success Rate</span>
							<span className="font-medium">{successRate}%</span>
						</div>
						<Progress value={successRate} className="h-2" />
					</div>
					<div className="space-y-2">
						<div className="flex items-center justify-between text-sm">
							<span className="text-muted-foreground">Average Rating</span>
							<span className="font-medium">{user.reputation.toFixed(1)} / 5.0</span>
						</div>
						<Progress value={user.reputation * 20} className="h-2" />
					</div>
					<div className="space-y-2">
						<div className="flex items-center justify-between text-sm">
							<span className="text-muted-foreground">Trade Volume</span>
							<span className="font-medium">{user.totalSwaps} swaps</span>
						</div>
						<Progress value={Math.min(user.totalSwaps * 3, 100)} className="h-2" />
					</div>
				</CardContent>
			</Card>

			{/* Tabs: Items & Reviews */}
			<Tabs defaultValue="items">
				<TabsList className="w-full">
					<TabsTrigger value="items" className="flex-1">
						Items ({availableItems.length})
					</TabsTrigger>
					<TabsTrigger value="reviews" className="flex-1">
						Reviews ({user.ratings.length})
					</TabsTrigger>
				</TabsList>

				<TabsContent value="items" className="mt-4">
					{availableItems.length === 0 ? (
						<div className="text-center py-8">
							<p className="text-muted-foreground">No items listed.</p>
							{isOwnProfile && (
								<Button variant="link" onClick={() => navigate("/list")}>
									List your first item
								</Button>
							)}
						</div>
					) : (
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
							{availableItems.map((item) => (
								<ItemCard key={item.id} item={item} />
							))}
						</div>
					)}
				</TabsContent>

				<TabsContent value="reviews" className="mt-4 space-y-3">
					{user.ratings.length === 0 ? (
						<p className="text-center text-muted-foreground py-8">No reviews yet.</p>
					) : (
						user.ratings.map((rating) => (
							<Card key={rating.id}>
								<CardContent className="pt-4 pb-4">
									<div className="flex items-start justify-between">
										<div className="flex items-center gap-2">
											<div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center">
												<User className="h-3.5 w-3.5 text-muted-foreground" />
											</div>
											<div>
												<button
													className="text-sm font-medium hover:text-primary"
													onClick={() => navigate(`/profile/${rating.fromUserId}`)}
												>
													@{rating.fromUsername}
												</button>
												<p className="text-[10px] text-muted-foreground">{rating.createdAt}</p>
											</div>
										</div>
										<div className="flex items-center">
											{[1, 2, 3, 4, 5].map((star) => (
												<Star
													key={star}
													className={`h-3.5 w-3.5 ${
														star <= rating.score
															? "fill-amber-400 text-amber-400"
															: "text-muted-foreground/30"
													}`}
												/>
											))}
										</div>
									</div>
									{rating.comment && (
										<div className="mt-2 flex gap-2">
											<MessageSquare className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
											<p className="text-sm text-muted-foreground">{rating.comment}</p>
										</div>
									)}
								</CardContent>
							</Card>
						))
					)}
				</TabsContent>
			</Tabs>
		</div>
	);
}
